// LSP-21256: launch-context telemetry — is this page running in a regular browser tab or as a
// Home Screen (installed) web app?
//
// A Home Screen web app looks like the native app to a customer, so "I used the app" can mean either.
// These fields describe the CURRENT running context only: an installed-but-unused web app changes
// nothing here, and missing telemetry never implies either the native app or a browser tab.
//
// Field meanings and support queries: docs/observability/launch-context-telemetry.md.
//
// Every function here is diagnostic-only and never throws — telemetry must not block navigation,
// capture, or upload.

/** Every CSS `display-mode` the spec defines, plus our two "could not tell" values. */
export type LaunchDisplayMode =
	| "fullscreen"
	| "standalone"
	| "minimal-ui"
	| "window-controls-overlay"
	| "picture-in-picture"
	| "browser"
	// matchMedia works but no display-mode query matched (an engine that predates the feature).
	| "unknown"
	// matchMedia itself is missing or threw.
	| "unsupported";

/** iOS `navigator.standalone`, kept as a string so the Datadog facet has one type. */
export type IosStandalone = "true" | "false" | "unsupported";

export type LaunchSurface = "home-screen" | "browser" | "unknown";

/**
 * The dashboard's surface as handed to the recorder. "not-provided" = no handoff parameter (a direct
 * recorder link, an emailed request, an older dashboard build); "unrecognized" = a value we did not write.
 */
export type OriginLaunchSurface = LaunchSurface | "not-provided" | "unrecognized";

export interface LaunchContext {
	displayMode: LaunchDisplayMode;
	iosStandalone: IosStandalone;
	launchSurface: LaunchSurface;
}

/** Query parameter the dashboard adds to the recorder URL. Carries no identifier, token or URL. */
export const ORIGIN_LAUNCH_SURFACE_PARAM = "originLaunchSurface";

// Checked in order; the first match wins. `browser` is last because it is the default mode.
const DISPLAY_MODES: readonly Exclude<LaunchDisplayMode, "unknown" | "unsupported">[] = [
	"fullscreen",
	"standalone",
	"minimal-ui",
	"window-controls-overlay",
	"picture-in-picture",
	"browser"
];

// Modes a browser tab never reports on its own. `fullscreen` is excluded: a desktop tab in F11
// fullscreen matches it too, so it cannot prove a Home Screen launch.
const HOME_SCREEN_DISPLAY_MODES: ReadonlySet<LaunchDisplayMode> = new Set(["standalone", "minimal-ui", "window-controls-overlay"]);

const LAUNCH_SURFACES: ReadonlySet<string> = new Set<LaunchSurface>(["home-screen", "browser", "unknown"]);

type LaunchContextWindow = Pick<Window, "matchMedia"> & {
	navigator?: Navigator & { standalone?: unknown };
};

export function detectDisplayMode(targetWindow: Partial<LaunchContextWindow> | undefined): LaunchDisplayMode {
	try {
		if (typeof targetWindow?.matchMedia !== "function") return "unsupported";
		for (const mode of DISPLAY_MODES) {
			if (targetWindow.matchMedia(`(display-mode: ${mode})`).matches) return mode;
		}
		return "unknown";
	} catch {
		return "unsupported";
	}
}

export function detectIosStandalone(targetWindow: Partial<LaunchContextWindow> | undefined): IosStandalone {
	try {
		const standalone = targetWindow?.navigator?.standalone;
		// Only WebKit on iOS/iPadOS defines it; everywhere else it is undefined, which is not "false".
		if (typeof standalone !== "boolean") return "unsupported";
		return standalone ? "true" : "false";
	} catch {
		return "unsupported";
	}
}

/** iOS uses both signals: either one proving standalone is enough. */
export function resolveLaunchSurface(displayMode: LaunchDisplayMode, iosStandalone: IosStandalone): LaunchSurface {
	if (iosStandalone === "true" || HOME_SCREEN_DISPLAY_MODES.has(displayMode)) return "home-screen";
	if (displayMode === "browser") return "browser";
	return "unknown";
}

export function detectLaunchContext(
	targetWindow: Partial<LaunchContextWindow> | undefined = typeof window !== "undefined" ? window : undefined
): LaunchContext {
	const displayMode = detectDisplayMode(targetWindow);
	const iosStandalone = detectIosStandalone(targetWindow);
	return {
		displayMode,
		iosStandalone,
		launchSurface: resolveLaunchSurface(displayMode, iosStandalone)
	};
}

/**
 * Add the dashboard's launch surface to a recorder URL. Returns the URL unchanged if it cannot be
 * parsed — a handoff with missing telemetry is always better than a handoff that does not happen.
 */
export function appendOriginLaunchSurface(
	url: string,
	surface: LaunchSurface,
	baseUrl: string | undefined = typeof window !== "undefined" ? window.location.origin : undefined
): string {
	if (!url) return url;
	try {
		const parsedUrl = new URL(url, baseUrl);
		parsedUrl.searchParams.set(ORIGIN_LAUNCH_SURFACE_PARAM, surface);
		return parsedUrl.toString();
	} catch {
		return url;
	}
}

/** Read the handoff parameter from THIS page load's query string only — never from storage. */
export function readOriginLaunchSurface(search: string | undefined): OriginLaunchSurface {
	try {
		const value = new URLSearchParams(search ?? "").get(ORIGIN_LAUNCH_SURFACE_PARAM);
		if (value === null || value === "") return "not-provided";
		return LAUNCH_SURFACES.has(value) ? (value as LaunchSurface) : "unrecognized";
	} catch {
		return "not-provided";
	}
}
